//App.js
// Updated: 2025-01-20 16:56:00 UTC+8
// Added: Website configuration system integration

import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import './App.css';
import Header from './components/web/Header/Header';
import Footer from './components/web/Footer/footer';
import HomePage from './components/web/HomePage/HomePage'; // Keep home page for immediate loading
import 'bootstrap/dist/css/bootstrap.min.css';
import CookieConsent from './components/web/CookieConsent/CookieConsent';
import { I18nextProvider } from 'react-i18next';
import i18n from '../src/components/web/i18n/i18n';
import { initPerformanceOptimizations } from './utils/performance';

// Configuration System Imports
import { ConfigProvider, useConfig } from './components/system/providers/ConfigProvider';
import { SEOHead } from './components/system/seo/SEOManager';

// Lazy load components for better performance
const News = lazy(() => import('./components/web/News/NewsPage'));
const PrivacyPolicy = lazy(() => import('./components/web/Privacy/privacy'));
const AboutusPage = lazy(() => import('./components/web/Aboutus/AboutusPage'));
const AI4GoodPage = lazy(() => import('./components/web/AI4Good/AI4GoodPage'));
const NewsEdit = lazy(() => import('./components/web/News/News_edit'));
const TermsOfUse = lazy(() => import('./components/web/TermsOfUse/TermsOfUse'));
const SvgMapComponent = lazy(() => import('./components/web/GBA/svgmap'));
const HongKongMap = lazy(() => import('./components/web/GBA/HongKongMap'));
const HongKongMap02 = lazy(() => import('./components/web/GBA/HongKongMap02'));
const ResourcesPage = lazy(() => import('./components/web/Resources/ResourcesPage'));
const ToolsPage = lazy(() => import('./components/web/Tools/ToolsPage'));
const Exhibitors = lazy(() => import('./components/web/Exhibitors/Exhibitors'));
const AdminRoute = lazy(() => import('./components/system/admin/AdminRoute'));
const NotFound = lazy(() => import('./components/Others/NotFound'));

// Loading component for Suspense
const LoadingSpinner = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

/**
 * Main App Component with Configuration System Integration
 */
const AppContent = () => {
  const { config, loading } = useConfig();

  // Initialize performance optimizations
  useEffect(() => {
    initPerformanceOptimizations();
  }, []);

  // Show loading spinner while configuration is loading
  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <I18nextProvider i18n={i18n}>
      <Router>
        {/* SEO Head Component - Dynamically manages meta tags */}
        <SEOHead config={config} />
        
        <CookieConsent />
        <div className="App">
          <Header />
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route exact path="/" element={<HomePage />} />
              <Route path="/news" element={<News key={i18n.language} />} />
              <Route path="/newsedit" element={<NewsEdit />} />
              <Route path="/Exhibitors" element={<Exhibitors key={i18n.language} />} />
              <Route path="/aboutus" element={<AboutusPage />} />
              <Route path="/AI4Good" element={<AI4GoodPage key={i18n.language} />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy key={i18n.language} />} />
              <Route path="/TermsOfUse" element={<TermsOfUse />} />
              <Route path="/svgmap" element={<SvgMapComponent width={1160} height={740} />} />
              <Route path="/hkmap" element={<HongKongMap />} />
              <Route path="/hkmap02" element={<HongKongMap02 />} />
              <Route path="/resources" element={<ResourcesPage />} />
              <Route path="/Tools" element={<ToolsPage />} />
              <Route path="/admin" element={<AdminRoute />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <Footer key={i18n.language} />
        </div>
      </Router>
    </I18nextProvider>
  );
};

/**
 * Root App Component with Configuration Provider
 */
function App() {
  return (
    <ConfigProvider>
      <AppContent />
    </ConfigProvider>
  );
}

export default App;